import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/tables/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Building2, Users, UserCheck, User } from "lucide-react";

import {
  Establishment,
  Staff,
  Manager,
  EstablishmentOwner,
  apiService,
  PaginatedResponse,
} from "@/services/api";
import { format } from "date-fns";
import { toast } from "sonner";
import { UserStatus, getUserStatusDisplayName } from "@/constants/enums";

// Data is now fetched from backend APIs (no mock data)

// Establishment columns
const getEstablishmentColumns = (
  navigate: (_path: string) => void,
  refresh: () => void
): ColumnDef<Establishment>[] => [
  {
    accessorKey: "establishmentName",
    header: "Establishment",
    cell: ({ row }) => {
      const establishment = row.original;
      return (
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={establishment.establishmentLogo?.fileUrl}
              alt={establishment.establishmentName}
            />
            <AvatarFallback>
              {establishment.establishmentName
                .split(" ")
                .map(n => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{establishment.establishmentName}</div>
            <div className="text-sm text-muted-foreground">{establishment.serviceType}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "specialty",
    header: "Specialty",
    cell: ({ row }) => row.original.specialty || "N/A",
  },
  {
    accessorKey: "yearOfExperience",
    header: "Experience",
    cell: ({ row }) => `${row.original.yearOfExperience || 0} years`,
  },
  {
    accessorKey: "address",
    header: "Location",
    cell: ({ row }) => {
      const address = row.original.address;
      return address ? `${address.city}, ${address.state}` : "N/A";
    },
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => format(new Date(row.original.createdAt), "MMM dd, yyyy"),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="View Establishment"
            onClick={() => {
              navigate(`/establishments/${row.original._id}`);
            }}
          >
            <Building2 className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="View Owner"
            onClick={() => {
              navigate(`/users/${row.original.userId}`);
            }}
          >
            <User className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Delete"
            onClick={async () => {
              try {
                const res = await apiService.deleteEstablishment(row.original._id);
                if (res.type === 0) {
                  toast.success("Establishment deleted");
                  refresh();
                } else {
                  toast.error(res.message || "Failed to delete establishment");
                }
              } catch {
                toast.error("Failed to delete establishment");
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
];

// Staff columns
const getStaffColumns = (
  navigate: (_path: string) => void,
  refresh: () => void
): ColumnDef<Staff>[] => [
  {
    accessorKey: "userId",
    header: "Staff Member",
    cell: ({ row }) => {
      const staff = row.original;
      const user = typeof staff.userId === "object" ? staff.userId : null;
      const fullName = user?.fullName || "N/A";
      
      return (
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarImage 
              src={user?.profilePicture?.fileUrl || staff.profilePicture?.fileUrl} 
              alt={fullName} 
            />
            <AvatarFallback>
              {fullName.split(" ").map(n => n[0]).join("").toUpperCase() || "ST"}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{fullName}</div>
            <div className="text-sm text-muted-foreground">{staff.designation || "Staff"}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "designation",
    header: "Role",
    cell: ({ row }) => row.original.designation || "N/A",
  },
  {
    accessorKey: "experience",
    header: "Experience",
    cell: ({ row }) => row.original.experience || "N/A",
  },
  {
    accessorKey: "hourlyRate",
    header: "Hourly Rate",
    cell: ({ row }) => `$${row.original.hourlyRate || 0}/hr`,
  },
  {
    accessorKey: "skills",
    header: "Skills",
    cell: ({ row }) => {
      const skills = row.original.skills;
      return skills && skills.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {skills.slice(0, 2).map((skill, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {skill}
            </Badge>
          ))}
          {skills.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{skills.length - 2} more
            </Badge>
          )}
        </div>
      ) : (
        "N/A"
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: "Joined",
    cell: ({ row }) => format(new Date(row.original.createdAt), "MMM dd, yyyy"),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const staff = row.original;
      const userIdStr = typeof staff.userId === "string" ? staff.userId : staff.userId._id;
      
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="View Profile"
            onClick={() => {
              navigate(`/users/${userIdStr}`);
            }}
          >
            <User className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Delete"
            onClick={async () => {
              try {
                const res = await apiService.deleteStaff(row.original._id);
                if (res.type === 0) {
                  toast.success("Staff deleted");
                  refresh();
                } else {
                  toast.error(res.message || "Failed to delete staff");
                }
              } catch {
                toast.error("Failed to delete staff");
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
];

// Manager columns
const getManagerColumns = (
  navigate: (_path: string) => void,
  refresh: () => void
): ColumnDef<Manager>[] => [
  {
    accessorKey: "userId",
      header: "Manager",
      cell: ({ row }) => {
        const manager = row.original;
      const user = typeof manager.userId === "object" && manager.userId !== null ? manager.userId : null;
      const fullName = user?.fullName || "N/A";
      
      return (
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarImage 
              src={user?.profilePicture?.fileUrl || manager.profilePicture?.fileUrl} 
              alt={fullName} 
            />
            <AvatarFallback>
              {fullName.split(" ").map(n => n[0]).join("").toUpperCase() || "MG"}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{fullName}</div>
            <div className="text-sm text-muted-foreground">
              {user?.email || "No email"}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "establishmentId",
    header: "Establishment",
    cell: ({ row }) => {
      const manager = row.original;
      const establishment = typeof manager.establishmentId === "object" && manager.establishmentId !== null 
        ? manager.establishmentId 
        : null;
      
      return establishment ? (
        <div className="flex items-center space-x-2">
          <Avatar className="h-6 w-6">
            <AvatarImage 
              src={establishment.establishmentLogo?.fileUrl} 
              alt={establishment.establishmentName} 
            />
            <AvatarFallback>
              {establishment.establishmentName.split(" ").map(n => n[0]).join("").toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{establishment.establishmentName}</div>
            <div className="text-xs text-muted-foreground">{establishment.serviceType}</div>
          </div>
        </div>
      ) : "N/A";
    },
  },
  {
    accessorKey: "experience",
    header: "Experience",
    cell: ({ row }) => row.original.experience || "N/A",
  },
  {
    accessorKey: "gender",
    header: "Gender",
    cell: ({ row }) => row.original.gender || "N/A",
  },
  {
    accessorKey: "createdAt",
    header: "Joined",
    cell: ({ row }) => {
      const manager = row.original;
      const user = typeof manager.userId === "object" && manager.userId !== null ? manager.userId : null;
      const createdAt = user?.createdAt || manager.createdAt;
      
      if (!createdAt) return "N/A";
      
      try {
        return format(new Date(createdAt), "MMM dd, yyyy");
      } catch {
        return "N/A";
      }
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const manager = row.original;
      const userIdStr = typeof manager.userId === "string" 
        ? manager.userId 
        : manager.userId?._id || "";
      const establishmentIdStr = typeof manager.establishmentId === "string" 
        ? manager.establishmentId 
        : manager.establishmentId?._id || "";
      
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="View Profile"
            onClick={() => {
              navigate(`/users/${userIdStr}`);
            }}
          >
            <User className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="View Establishment"
            onClick={() => {
              navigate(`/establishments/${establishmentIdStr}`);
            }}
          >
            <Building2 className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Delete"
            onClick={async () => {
              try {
                const res = await apiService.deleteManager(row.original._id);
                if (res.type === 0) {
                  toast.success("Manager deleted");
                  refresh();
                } else {
                  toast.error(res.message || "Failed to delete manager");
                }
              } catch {
                toast.error("Failed to delete manager");
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
];

// Establishment Owner columns
const getEstablishmentOwnerColumns = (
  navigate: (_path: string) => void,
  refresh: () => void
): ColumnDef<EstablishmentOwner>[] => [
  {
    accessorKey: "fullName",
    header: "Owner",
    cell: ({ row }) => {
      const owner = row.original;
      return (
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={owner.profilePicture?.fileUrl} alt={owner.fullName} />
            <AvatarFallback>
              {owner.fullName
                .split(" ")
                .map(n => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{owner.fullName}</div>
            <div className="text-sm text-muted-foreground">{owner.email}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "establishmentCount",
    header: "Establishments",
    cell: ({ row }) => (
      <Badge variant="outline" className="bg-blue-50 text-blue-700">
        {row.original.establishmentCount}
      </Badge>
    ),
  },
  {
    accessorKey: "managerCount",
    header: "Managers",
    cell: ({ row }) => (
      <Badge variant="outline" className="bg-green-50 text-green-700">
        {row.original.managerCount}
      </Badge>
    ),
  },
  {
    accessorKey: "userStatus",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.userStatus;
      const statusText = getUserStatusDisplayName(status);
      
      let badgeClass = "";
      switch (status) {
        case UserStatus.VERIFIED:
          badgeClass = "bg-green-100 text-green-800";
          break;
        case UserStatus.PENDING_VERIFICATION:
          badgeClass = "bg-yellow-100 text-yellow-800";
          break;
        case UserStatus.CREATED:
          badgeClass = "bg-blue-100 text-blue-800";
          break;
        case UserStatus.BLOCKED:
          badgeClass = "bg-red-100 text-red-800";
          break;
        case UserStatus.DELETED:
          badgeClass = "bg-gray-100 text-gray-800";
          break;
        default:
          badgeClass = "bg-gray-100 text-gray-800";
      }
      
      return <Badge className={badgeClass}>{statusText}</Badge>;
    },
  },
  {
    accessorKey: "createdAt",
    header: "Joined",
    cell: ({ row }) => format(new Date(row.original.createdAt), "MMM dd, yyyy"),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="View Profile"
            onClick={() => {
              navigate(`/users/${row.original._id}`);
            }}
          >
            <User className="mr-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Delete"
            onClick={async () => {
              try {
                const res = await apiService.deleteEstablishmentOwner(row.original._id);
                if (res.type === 0) {
                  toast.success("Owner deleted");
                  refresh();
                } else {
                  toast.error(res.message || "Failed to delete owner");
                }
              } catch {
                toast.error("Failed to delete owner");
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
];

export function Establishments() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("establishments");
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [establishmentOwners, setEstablishmentOwners] = useState<EstablishmentOwner[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadData() {
    setLoading(true);
    try {
      const [estRes, staffRes, managerRes, ownerRes] = await Promise.all([
        apiService.getAllEstablishments({ page: 1 }),
        apiService.getAllStaff({ page: 1 }),
        apiService.getAllManagers({ page: 1 }),
        apiService.getAllEstablishmentOwners({ page: 1 }),
      ]);

      if (estRes.type === 0) {
        const data = (estRes.data as PaginatedResponse<Establishment>).history;
        setEstablishments(data);
      } else {
        toast.error(estRes.message || "Failed to load establishments");
      }

      if (staffRes.type === 0) {
        const data = (staffRes.data as PaginatedResponse<Staff>).history;
        setStaff(data);
      } else {
        toast.error(staffRes.message || "Failed to load staff");
      }

      if (managerRes.type === 0) {
        const data = (managerRes.data as PaginatedResponse<Manager>).history;
        setManagers(data);
      } else {
        toast.error(managerRes.message || "Failed to load managers");
      }

      if (ownerRes.type === 0) {
        const data = (ownerRes.data as PaginatedResponse<EstablishmentOwner>).history;
        setEstablishmentOwners(data);
      } else {
        toast.error(ownerRes.message || "Failed to load owners");
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Establishments & Staff</h1>
          <p className="text-muted-foreground">
            Manage establishments, staff members, and establishment owners
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Establishments & Staff</h1>
        <p className="text-muted-foreground">
          Manage establishments, staff members, managers, and establishment owners
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Establishments</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{establishments.length}</div>
            <p className="text-xs text-muted-foreground">Active establishments in the platform</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Staff</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{staff.length}</div>
            <p className="text-xs text-muted-foreground">Registered staff members</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Managers</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{managers.length}</div>
            <p className="text-xs text-muted-foreground">Active managers</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Establishment Owners</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{establishmentOwners.length}</div>
            <p className="text-xs text-muted-foreground">Active establishment owners</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="establishments" className="flex items-center space-x-2">
            <Building2 className="h-4 w-4" />
            <span>Establishments</span>
          </TabsTrigger>
          <TabsTrigger value="staff" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Staff</span>
          </TabsTrigger>
          <TabsTrigger value="managers" className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>Managers</span>
          </TabsTrigger>
          <TabsTrigger value="owners" className="flex items-center space-x-2">
            <UserCheck className="h-4 w-4" />
            <span>Establishment Owners</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="establishments" className="space-y-4">
          <DataTable
            columns={getEstablishmentColumns(navigate, () => {
              void loadData();
            })}
            data={establishments}
            searchKey="establishmentName"
            searchPlaceholder="Search establishments..."
            pageSize={10}
          />
        </TabsContent>

        <TabsContent value="staff" className="space-y-4">
          <DataTable
            columns={getStaffColumns(navigate, () => {
              void loadData();
            })}
            data={staff}
            searchKey="userId"
            searchPlaceholder="Search staff..."
            pageSize={10}
          />
        </TabsContent>

        <TabsContent value="managers" className="space-y-4">
          <DataTable
            columns={getManagerColumns(navigate, () => {
              void loadData();
            })}
            data={managers}
            searchKey="userId"
            searchPlaceholder="Search managers..."
            pageSize={10}
          />
        </TabsContent>

        <TabsContent value="owners" className="space-y-4">
          <DataTable
            columns={getEstablishmentOwnerColumns(navigate, () => {
              void loadData();
            })}
            data={establishmentOwners}
            searchKey="fullName"
            searchPlaceholder="Search establishment owners..."
            pageSize={10}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
